import React, { useState } from "react";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  ArrowRight,
  ArrowLeft,
  ShieldCheck,
  CheckCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import AuthLayout, { fieldClass, labelClass, fieldIconClass } from "../components/Layout/AuthLayout";
import api from "../utils/axios";

const STEPS = {
  EMAIL: 1,
  OTP: 2,
  PASSWORD: 3,
  DONE: 4,
};

const VendorForgotPassword = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState(STEPS.EMAIL);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const getApiMessage = (err, fallback) =>
    err?.response?.data?.message || fallback;

  // Step 1 — request reset OTP
  const handleSendOtp = async () => {
    setError("");
    if (!email.trim()) return setError("Email is required");
    if (!/\S+@\S+\.\S+/.test(email)) return setError("Email is invalid");

    setLoading(true);
    try {
      await api.post("/vendors/forgot-password", { email: email.trim() });
      toast.success("If the email exists, a reset code has been sent.");
      setStep(STEPS.OTP);
    } catch (err) {
      setError(getApiMessage(err, "Failed to send reset code. Try again."));
    } finally {
      setLoading(false);
    }
  };

  // Step 2 — verify OTP
  const handleVerifyOtp = async () => {
    setError("");
    if (!otp.trim()) return setError("Enter the code sent to your email");

    setLoading(true);
    try {
      await api.post("/vendors/verify-reset-otp", {
        email: email.trim(),
        otp: otp.trim(),
      });
      setStep(STEPS.PASSWORD);
    } catch (err) {
      setError(getApiMessage(err, "Invalid or expired code."));
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResend = async () => {
    setError("");
    setLoading(true);
    try {
      await api.post("/vendors/forgot-password", { email: email.trim() });
      toast.success("A new code has been sent.");
    } catch (err) {
      setError(getApiMessage(err, "Failed to resend code."));
    } finally {
      setLoading(false);
    }
  };

  // Step 3 — set new password
  const handleResetPassword = async () => {
    setError("");
    if (newPassword.length < 6)
      return setError("Password must be at least 6 characters");
    if (newPassword !== confirmPassword)
      return setError("Passwords do not match");

    setLoading(true);
    try {
      await api.post("/vendors/reset-password", {
        email: email.trim(),
        otp: otp.trim(),
        newPassword,
      });
      setStep(STEPS.DONE);
      toast.success("Password reset successful!");
    } catch (err) {
      setError(getApiMessage(err, "Failed to reset password."));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e, action) => {
    if (e.key === "Enter") action();
  };

  const inputClass = fieldClass(false);

  const primaryBtn =
    "group flex w-full items-center justify-center gap-2 h-12 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 px-5 text-[15px] font-['Manrope'] font-semibold text-white shadow-lg shadow-purple-600/20 transition-all duration-200 hover:shadow-xl hover:shadow-purple-600/30 disabled:cursor-not-allowed disabled:opacity-70 focus:outline-none focus:ring-2 focus:ring-purple-500/40";

  const Spinner = ({ label }) => (
    <>
      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
      <span>{label}</span>
    </>
  );

  return (
    <AuthLayout>
            <div className="space-y-5">
              {/* Heading */}
              <div className="mb-7">
                <h1 className="font-['Manrope'] text-3xl font-extrabold tracking-tight text-gray-900">
                  {step === STEPS.DONE ? "All Set!" : "Reset Password"}
                </h1>
                <p className="mt-2 text-[15px] text-gray-500">
                  {step === STEPS.EMAIL &&
                    "Enter your account email and we'll send you a reset code."}
                  {step === STEPS.OTP &&
                    `Enter the 6-digit code sent to ${email}.`}
                  {step === STEPS.PASSWORD && "Choose a new password."}
                  {step === STEPS.DONE &&
                    "Your password has been reset successfully."}
                </p>
              </div>

              {/* Error */}
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm font-['Inter']">{error}</p>
                </div>
              )}

              {/* Step 1: Email */}
              {step === STEPS.EMAIL && (
                <>
                  <div>
                    <label className={labelClass}>
                      Email Address
                    </label>
                    <div className="group/field relative">
                      <Mail className={fieldIconClass} />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyPress={(e) => handleKeyPress(e, handleSendOtp)}
                        className={inputClass}
                        placeholder="Enter your email address"
                        autoComplete="email"
                      />
                    </div>
                  </div>
                  <button
                    onClick={handleSendOtp}
                    disabled={loading}
                    className={primaryBtn}
                  >
                    {loading ? (
                      <Spinner label="Sending..." />
                    ) : (
                      <>
                        <span>Send Reset Code</span>
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                      </>
                    )}
                  </button>
                </>
              )}

              {/* Step 2: OTP */}
              {step === STEPS.OTP && (
                <>
                  <div>
                    <label className={labelClass}>
                      Verification Code
                    </label>
                    <div className="group/field relative">
                      <ShieldCheck className={fieldIconClass} />
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={otp}
                        onChange={(e) =>
                          setOtp(e.target.value.replace(/\D/g, ""))
                        }
                        onKeyPress={(e) => handleKeyPress(e, handleVerifyOtp)}
                        className={`${inputClass} tracking-[0.5em] text-center font-semibold`}
                        placeholder="000000"
                      />
                    </div>
                  </div>
                  <button
                    onClick={handleVerifyOtp}
                    disabled={loading}
                    className={primaryBtn}
                  >
                    {loading ? (
                      <Spinner label="Verifying..." />
                    ) : (
                      <>
                        <span>Verify Code</span>
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                      </>
                    )}
                  </button>
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={handleResend}
                      disabled={loading}
                      className="text-sm font-semibold text-gray-900 underline decoration-purple-300 decoration-2 underline-offset-4 transition-colors hover:decoration-purple-600"
                    >
                      Didn't get a code? Resend
                    </button>
                  </div>
                </>
              )}

              {/* Step 3: New Password */}
              {step === STEPS.PASSWORD && (
                <>
                  <div>
                    <label className={labelClass}>
                      New Password
                    </label>
                    <div className="group/field relative">
                      <Lock className={fieldIconClass} />
                      <input
                        type={showPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className={`${inputClass} !pr-12`}
                        placeholder="Enter new password"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-gray-600"
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                      >
                        {showPassword ? (
                          <EyeOff className="h-[18px] w-[18px]" />
                        ) : (
                          <Eye className="h-[18px] w-[18px]" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>
                      Confirm Password
                    </label>
                    <div className="group/field relative">
                      <Lock className={fieldIconClass} />
                      <input
                        type={showPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        onKeyPress={(e) =>
                          handleKeyPress(e, handleResetPassword)
                        }
                        className={inputClass}
                        placeholder="Re-enter new password"
                        autoComplete="new-password"
                      />
                    </div>
                  </div>
                  <button
                    onClick={handleResetPassword}
                    disabled={loading}
                    className={primaryBtn}
                  >
                    {loading ? (
                      <Spinner label="Resetting..." />
                    ) : (
                      <>
                        <span>Reset Password</span>
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                      </>
                    )}
                  </button>
                </>
              )}

              {/* Step 4: Done */}
              {step === STEPS.DONE && (
                <div className="text-center space-y-6">
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                    <CheckCircle className="h-9 w-9 text-green-600" />
                  </div>
                  <button
                    onClick={() => navigate("/vendor-signin")}
                    className={primaryBtn}
                  >
                    <span>Back to Sign In</span>
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </button>
                </div>
              )}

              {/* Back to sign in */}
              {step !== STEPS.DONE && (
                <button
                  type="button"
                  onClick={() => navigate("/vendor-signin")}
                  className="w-full flex items-center justify-center space-x-1 text-sm text-gray-600 hover:text-gray-800 font-medium"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Back to Sign In</span>
                </button>
              )}
            </div>
    </AuthLayout>
  );
};

export default VendorForgotPassword;
